// biome-ignore-all lint/suspicious/noConsole: SSE stream error reporting until Sentry SDK is wired up
import { extractSSEErrorMessage } from '@utils/extractSSEErrorMessage';
import { normalizeRole } from '@utils/roleUtils';
import { useCallback, useEffect, useRef } from 'react';
import { API_BASE, SENTINEL_SSE_EVENTS, SSE_CONFIG } from 'src/constants/sentinel';
import {
	addOrUpdateTimelineEvent,
	addTimelineEvent,
	updateClaimVerification,
	verifyPhaseCompleted,
	verifyPhaseFailed,
	verifyPhaseStarted
} from 'src/store/slices/sentinel';
import { EvaluatedClaim } from 'src/types/sentinel';
import { useAppDispatch, useAppSelector } from 'universal-scripts';

type ClaimProcessState = 'idle' | 'processing' | 'waiting_retry' | 'completed' | 'failed';

export function useStreamVerify(claims: EvaluatedClaim[] | null, lang: string = 'en', refreshCache: boolean = false) {
	const dispatch = useAppDispatch();
	const processId = useAppSelector((state) => state.sentinel.processId);
	const status = useAppSelector((state) => state.sentinel.phases.verify.status);

	const activeSourcesRef = useRef<Map<number, EventSource>>(new Map());
	const connectionTimeoutsRef = useRef<Map<number, NodeJS.Timeout>>(new Map());
	const retryTimeoutsRef = useRef<Map<number, NodeJS.Timeout>>(new Map());
	const retryCountsRef = useRef<Map<number, number>>(new Map());
	const claimsStatusRef = useRef<Map<number, ClaimProcessState>>(new Map());
	const stepsProgressRef = useRef<Map<string, Set<number>>>(new Map());
	const isMountedRef = useRef(false);
	const hasStartedRef = useRef(false);

	const cleanup = useCallback(() => {
		isMountedRef.current = false;
		for (const t of connectionTimeoutsRef.current.values()) clearTimeout(t);
		for (const t of retryTimeoutsRef.current.values()) clearTimeout(t);
		for (const es of activeSourcesRef.current.values()) es.close();
		activeSourcesRef.current.clear();
		connectionTimeoutsRef.current.clear();
		retryTimeoutsRef.current.clear();
	}, []);

	useEffect(() => {
		if (!claims || claims.length === 0 || !processId) return;
		if (hasStartedRef.current) return;
		if (status === 'completed' || status === 'error') return;

		hasStartedRef.current = true;
		isMountedRef.current = true;

		stepsProgressRef.current.clear();
		retryCountsRef.current.clear();
		claimsStatusRef.current.clear();

		for (const c of claims) claimsStatusRef.current.set(c.id!, 'idle');

		if (status === 'idle') {
			dispatch(verifyPhaseStarted());
			dispatch(addTimelineEvent({ phase: 'verify', eventType: 'phase_start' }));
		}

		const MaxParallel = 5;
		let currentIndex = 0;
		const totalClaims = claims.length;

		const checkAllFinished = () => {
			const allStatuses = Array.from(claimsStatusRef.current.values());
			const isStillWorking = allStatuses.some((s) => s === 'processing' || s === 'waiting_retry' || s === 'idle');

			if (isStillWorking) return;

			const successCount = allStatuses.filter((s) => s === 'completed').length;

			if (successCount === totalClaims) {
				stepsProgressRef.current.forEach((_, stepId) => {
					dispatch(
						addOrUpdateTimelineEvent({
							phase: 'verify',
							eventType: 'step_update',
							stepId: stepId,
							stepStatus: 'completed',
							message: ` (${totalClaims}/${totalClaims})`
						})
					);
				});
			}

			dispatch(addTimelineEvent({ phase: 'verify', eventType: 'phase_complete' }));

			if (successCount > 0) {
				dispatch(verifyPhaseCompleted());
			} else {
				const msg = 'Unable to verify any claims due to network errors.';
				dispatch(addTimelineEvent({ phase: 'verify', eventType: 'error', message: msg }));
				dispatch(verifyPhaseFailed(msg));
			}
		};

		const verifyNextClaim = () => {
			if (!isMountedRef.current) return;

			if (currentIndex >= totalClaims) {
				checkAllFinished();
				return;
			}

			const claim = claims[currentIndex];
			const claimId = claim.id!;
			currentIndex++;

			const connectClaim = () => {
				if (!isMountedRef.current) return;

				const existingEs = activeSourcesRef.current.get(claimId);
				if (existingEs) existingEs.close();

				claimsStatusRef.current.set(claimId, 'processing');

				const handleErrorOrRetry = (msg: string) => {
					activeSourcesRef.current.delete(claimId);
					const timeout = connectionTimeoutsRef.current.get(claimId);
					if (timeout) clearTimeout(timeout);

					const currentStatus = claimsStatusRef.current.get(claimId);
					if (currentStatus === 'completed' || currentStatus === 'waiting_retry' || currentStatus === 'failed') return;

					const currentRetries = retryCountsRef.current.get(claimId) || 0;

					if (currentRetries < SSE_CONFIG.MAX_RETRIES) {
						claimsStatusRef.current.set(claimId, 'waiting_retry');
						retryCountsRef.current.set(claimId, currentRetries + 1);

						const retryTimeout = setTimeout(() => connectClaim(), SSE_CONFIG.RETRY_DELAY_MS);
						retryTimeoutsRef.current.set(claimId, retryTimeout);
					} else {
						dispatch(addTimelineEvent({ phase: 'verify', eventType: 'error', message: msg }));
						claimsStatusRef.current.set(claimId, 'failed');
						verifyNextClaim();
					}
				};

				const url = `${API_BASE}/verify-claim?lang=${lang}&claim=${encodeURIComponent(claim.claim)}${refreshCache ? '&refresh_cache=true' : ''}`;

				try {
					const eventSource = new EventSource(url);
					activeSourcesRef.current.set(claimId, eventSource);

					const resetConnectionTimeout = () => {
						const existingTimeout = connectionTimeoutsRef.current.get(claimId);
						if (existingTimeout) clearTimeout(existingTimeout);

						const timeout = setTimeout(() => {
							if (!isMountedRef.current) return;
							eventSource.close();
							handleErrorOrRetry('Timeout waiting for data');
						}, SSE_CONFIG.CONNECTION_TIMEOUT_MS);

						connectionTimeoutsRef.current.set(claimId, timeout);
					};

					resetConnectionTimeout();

					eventSource.addEventListener(SENTINEL_SSE_EVENTS.STATUS_CHANGE, (event) => {
						resetConnectionTimeout();
						try {
							const data = JSON.parse(event.data);
							const stepId = data.step_id;

							if (!stepsProgressRef.current.has(stepId)) {
								stepsProgressRef.current.set(stepId, new Set());
							}
							const stepSet = stepsProgressRef.current.get(stepId)!;

							if (data.new_status === 'completed') {
								stepSet.add(claimId);
							}
							const completedCountForStep = stepSet.size;
							const isGlobalStepComplete = completedCountForStep === totalClaims;

							dispatch(
								addOrUpdateTimelineEvent({
									phase: 'verify',
									eventType: 'step_update',
									stepId: stepId,
									stepStatus: isGlobalStepComplete ? 'completed' : 'in_progress',
									role: normalizeRole(data.role),
									message: ` (${completedCountForStep}/${totalClaims})`
								})
							);
						} catch (e) {
							console.error(e);
						}
					});

					eventSource.addEventListener('initial_review', (event) => {
						resetConnectionTimeout();
						try {
							const data = JSON.parse(event.data);
							dispatch(updateClaimVerification({ id: claimId, data: { conclusion: data.conclusion } }));
						} catch (e) {
							console.error(e);
						}
					});

					eventSource.addEventListener('internet_sources', (event) => {
						resetConnectionTimeout();
						try {
							const data = JSON.parse(event.data);
							if (data.sources?.length) {
								dispatch(updateClaimVerification({ id: claimId, data: { newsArticles: data.sources } }));
							}
						} catch (e) {
							console.error(e);
						}
					});

					eventSource.addEventListener('news_sources', (event) => {
						resetConnectionTimeout();
						try {
							const data = JSON.parse(event.data);
							if (data.sources?.length) {
								dispatch(updateClaimVerification({ id: claimId, data: { newsArticles: data.sources } }));
							}
						} catch (e) {
							console.error(e);
						}
					});

					eventSource.addEventListener('fact_checking_sources', (event) => {
						resetConnectionTimeout();
						try {
							const data = JSON.parse(event.data);
							dispatch(updateClaimVerification({ id: claimId, data: { factCheckSources: data.sources || [] } }));
						} catch (e) {
							console.error(e);
						}
					});

					eventSource.addEventListener('final_review', (event) => {
						const timeout = connectionTimeoutsRef.current.get(claimId);
						if (timeout) clearTimeout(timeout);
						try {
							const data = JSON.parse(event.data);
							dispatch(
								updateClaimVerification({
									id: claimId,
									data: { conclusion: data.conclusion, reasoning: data.reasoning }
								})
							);
						} catch (e) {
							console.error(e);
						}

						claimsStatusRef.current.set(claimId, 'completed');
						eventSource.close();
						activeSourcesRef.current.delete(claimId);
						verifyNextClaim();
					});

					eventSource.addEventListener(SENTINEL_SSE_EVENTS.ERROR, (event) => {
						const errorMsg = extractSSEErrorMessage(event);
						eventSource.close();
						handleErrorOrRetry(errorMsg);
					});

					eventSource.onerror = () => {
						eventSource.close();
						handleErrorOrRetry('Connection error');
					};
				} catch (err) {
					handleErrorOrRetry('Failed to create EventSource');
					console.error('Failed to create EventSource', err);
				}
			};

			connectClaim();
		};

		for (let i = 0; i < Math.min(MaxParallel, totalClaims); i++) {
			verifyNextClaim();
		}

		return () => {
			cleanup();
			hasStartedRef.current = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [claims, processId, dispatch, cleanup, refreshCache]);

	return { disconnect: cleanup };
}
